import { useEffect, useRef, useCallback } from 'react';
import { getSocket } from '@/lib/socket';
import { usePlayerStore } from '@/stores/playerStore';
import { useUserStore } from '@/stores/userStore';
import { useRoomStore } from '@/stores/roomStore';
import type { SyncCommand } from '@/types';

export const useYouTubePlayer = (containerRef: React.RefObject<HTMLDivElement>) => {
  const socket = getSocket();
  const { player, setPlayer, setReady, setPosition, setIsPlaying, setDuration, setVideoId, videoId, position, isPlaying: shouldBePlaying } = usePlayerStore();
  const { timeOffset } = useUserStore();
  const { myRole } = useRoomStore();
  const stateReportInterval = useRef<number | null>(null);
  const lastLoadedVideoId = useRef<string | null>(null);
  const hasRequestedNext = useRef<boolean>(false);
  const loadedFromSyncCommand = useRef<boolean>(false);

  const loadYouTubeAPI = useCallback(() => {
    if (window.YT) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        resolve();
      };
    });
  }, []);

  const initPlayer = useCallback(() => {
    if (!window.YT || !containerRef.current) return;

    new window.YT.Player(containerRef.current, {
      height: '100%',
      width: '100%',
      playerVars: {
        autoplay: 0,
        controls: 1,
        disablekb: 0,
        fs: 1,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
      },
      events: {
        onReady: (event) => {
          console.log('YouTube player ready');
          setPlayer(event.target);
          setReady(true);
          // Don't set volume here - will be set when a video is loaded
        },
        onStateChange: (event) => {
          console.log('Player state changed:', event.data);

          if (event.data === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
            // Reset the auto-next flag when a new video starts playing
            hasRequestedNext.current = false;

            // Set volume when video starts playing (first time a video is loaded)
            try {
              // Get current volume from store instead of closure
              const currentStoreVolume = usePlayerStore.getState().volume;
              const currentPlayerVolume = event.target.getVolume();
              if (currentPlayerVolume !== currentStoreVolume) {
                event.target.setVolume(currentStoreVolume);
              }
            } catch (error) {
              // Ignore volume errors
              console.log('Could not set volume on play:', error);
            }
          } else if (event.data === window.YT.PlayerState.PAUSED) {
            setIsPlaying(false);
          } else if (event.data === window.YT.PlayerState.ENDED) {
            setIsPlaying(false);

            // Only owner/DJ should trigger auto-next to avoid duplicate requests
            const canControlPlayback = myRole === 'owner' || myRole === 'dj';

            if (canControlPlayback && !hasRequestedNext.current) {
              hasRequestedNext.current = true;
              console.log('Video ended, requesting next track');
              socket.emit('playlist:next');

              // Reset flag after a delay
              setTimeout(() => {
                hasRequestedNext.current = false;
              }, 2000);
            }
          }
        },
        onError: (event) => {
          console.error('YouTube player error:', event.data);
          const errorMessages: { [key: number]: string } = {
            2: 'Video cannot be embedded',
            5: 'HTML5 player error',
            100: 'Video not found or private',
            101: 'Embedding not allowed by owner',
            150: 'Embedding not allowed by owner'
          };
          const message = errorMessages[event.data] || `Unknown error (${event.data})`;

          // Get current video ID from store to show helpful message
          const currentVideoId = usePlayerStore.getState().videoId;
          const ytMusicHint = event.data === 2 && currentVideoId
            ? `<br><small>YouTube Music videos may not work. Try the regular YouTube link: <a href="https://youtube.com/watch?v=${currentVideoId}" target="_blank" style="color: #fff; text-decoration: underline;">youtube.com/watch?v=${currentVideoId}</a></small>`
            : '<br><small>Try a different video or check if it exists</small>';

          // Show error to user
          const errorDiv = document.createElement('div');
          errorDiv.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #ef4444; color: white; padding: 16px; border-radius: 8px; z-index: 9999; max-width: 400px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);';
          errorDiv.innerHTML = `<strong>YouTube Error:</strong> ${message}${ytMusicHint}`;
          document.body.appendChild(errorDiv);
          setTimeout(() => errorDiv.remove(), 8000);

          setIsPlaying(false);
        },
      },
    });
  }, [containerRef, setPlayer, setReady, setIsPlaying]);

  // Handle sync commands from server
  useEffect(() => {
    const handleSyncCommand = (command: SyncCommand) => {
      if (!player) return;

      const now = Date.now();
      const adjustedExecuteAt = command.executeAt + timeOffset;
      const delay = Math.max(0, adjustedExecuteAt - now);

      console.log(`Sync command: ${command.action}, delay: ${delay}ms`);

      // For play commands, set flags IMMEDIATELY before any async operations
      if (command.action === 'play' && command.videoId) {
        lastLoadedVideoId.current = command.videoId;
        loadedFromSyncCommand.current = true;
        // Update videoId in store immediately to prevent other handlers from interfering
        setVideoId(command.videoId);
        setPosition(command.position);
        setIsPlaying(true);
      }

      setTimeout(() => {
        try {
          switch (command.action) {
            case 'play':
              if (command.videoId) {
                console.log(`Loading video: ${command.videoId} at position ${command.position}`);
                // Flags and state already set above, just load the video
                player.loadVideoById(command.videoId, command.position);

                // Explicitly call playVideo after a short delay to ensure playback starts
                setTimeout(() => {
                  try {
                    const state = player.getPlayerState();
                    console.log(`Player state after load: ${state}`);
                    // If not playing (state 1), try to start
                    if (state !== 1) {
                      console.log('Video not playing after load, calling playVideo()');
                      player.playVideo();
                    }
                  } catch (err) {
                    console.log('Could not verify/start playback:', err);
                  }
                }, 1000);

                // Reset the flag after a delay to allow normal videoId changes later
                setTimeout(() => {
                  loadedFromSyncCommand.current = false;
                }, 2000);
              }
              break;
            case 'pause':
              console.log(`Pausing at position ${command.position}`);
              setIsPlaying(false);
              player.pauseVideo();
              if (command.position !== undefined && command.position >= 0) {
                player.seekTo(command.position, true);
              }
              break;
            case 'resume':
              console.log(`Resuming at position ${command.position}`);
              setIsPlaying(true);
              if (command.position !== undefined && command.position >= 0) {
                player.seekTo(command.position, true);
              }
              player.playVideo();
              break;
            case 'seek':
              console.log(`Seeking to position ${command.position}`);
              if (command.position !== undefined && command.position >= 0) {
                player.seekTo(command.position, true);
              }
              break;
          }
        } catch (error) {
          console.error(`Error executing sync command ${command.action}:`, error);
        }
      }, delay);
    };

    socket.on('sync:command', handleSyncCommand);

    return () => {
      socket.off('sync:command', handleSyncCommand);
    };
  }, [socket, player, timeOffset, setVideoId, setIsPlaying, setPosition]);

  // Load video when videoId changes (e.g., when joining a room with an active video)
  useEffect(() => {
    if (!player || !videoId) return;

    // Skip if we already loaded this video (prevents re-loading on position/isPlaying changes)
    if (lastLoadedVideoId.current === videoId) {
      console.log('Video already loaded via this effect, skipping');
      return;
    }

    // Skip if this video was just loaded by a sync:command (prevents race condition with old position)
    if (loadedFromSyncCommand.current) {
      console.log('Video loading handled by sync:command, skipping effect');
      return;
    }

    // Check if the player already has this video loaded
    try {
      const loadedVideoUrl = player.getVideoUrl();
      if (loadedVideoUrl && loadedVideoUrl.includes(videoId)) {
        console.log('Video already loaded in player, updating tracking');
        lastLoadedVideoId.current = videoId;
        return;
      }
    } catch (error) {
      // Player might not have a video loaded yet
    }

    // Load the video at the current position
    console.log(`Loading video on videoId change: ${videoId} at position ${position}, shouldPlay=${shouldBePlaying}`);
    lastLoadedVideoId.current = videoId;

    try {
      if (shouldBePlaying) {
        // Load and immediately try to play
        // Note: Browser autoplay blocking may prevent this, requiring user interaction
        player.loadVideoById(videoId, position);

        // Try to ensure playback starts after a delay
        setTimeout(() => {
          try {
            const state = player.getPlayerState();
            // If not playing (state 1), try to start
            if (state !== 1) {
              console.log('Video not playing, attempting to start playback');
              player.playVideo();
            }
          } catch (err) {
            console.log('Could not verify/start playback:', err);
          }
        }, 1500);

        console.log('Video loaded, attempting auto-play');
      } else {
        // If should be paused, use cueVideoById to load without playing
        player.cueVideoById(videoId, position);
        console.log('Video cued (not playing)');
      }
    } catch (error) {
      console.error('Error loading video:', error);
    }
  }, [player, videoId, position, shouldBePlaying]);

  // Report player state periodically
  useEffect(() => {
    if (!player || !socket.connected) return;

    // Clear any existing interval before creating a new one
    if (stateReportInterval.current) {
      clearInterval(stateReportInterval.current);
    }

    stateReportInterval.current = setInterval(() => {
      try {
        const currentTime = player.getCurrentTime();
        const playerState = player.getPlayerState();

        setPosition(currentTime);

        if (player.getDuration()) {
          setDuration(player.getDuration());
        }

        // Report state to server
        socket.emit('sync:state', {
          position: currentTime,
          state: playerState,
        });
      } catch (error) {
        console.error('Error reporting state:', error);
      }
    }, 5000); // Every 5 seconds

    return () => {
      if (stateReportInterval.current) {
        clearInterval(stateReportInterval.current);
        stateReportInterval.current = null;
      }
    };
  }, [player, socket, setPosition, setDuration]);

  // Initialize YouTube API and player
  useEffect(() => {
    loadYouTubeAPI().then(() => {
      initPlayer();
    });

    return () => {
      if (player) {
        player.destroy();
      }
      if (stateReportInterval.current) {
        clearInterval(stateReportInterval.current);
      }
    };
  }, [loadYouTubeAPI, initPlayer]);

  return { player };
};
